import {
  SvcCreateStockNotification,
  SvcCreateStockNotificationForCurrentUser,
  SvcGetStockNotification,
  SvcGetStockNotificationForCurrentUser,
} from "@springtree/eva-services-core";
import { MutateOptions, useMutation } from "@tanstack/react-query";
import { useRouter } from "next/router";

import { useAuthContext } from "~/contexts/auth";
import { useEvaService } from "~/hooks/use-eva-service";
import Eva from "~/services/eva";

type UseNotifyMeProps = {
  productId: string;
  emailAddress?: string;
};

export function useNotifyMe({ productId, emailAddress }: UseNotifyMeProps) {
  const router = useRouter();
  const { user } = useAuthContext();
  const guestStockNotificationsGet = useEvaService(SvcGetStockNotification);
  const userStockNotificationsGet = useEvaService(SvcGetStockNotificationForCurrentUser);
  const guestStockNotificationsCreate = useEvaService(SvcCreateStockNotification);
  const userStockNotificationCreate = useEvaService(SvcCreateStockNotificationForCurrentUser);

  const getStockNotificationForCurrentUser = useMutation(
    async () => await userStockNotificationsGet({ ProductID: productId })
  );

  const getStockNotificationGuest = useMutation(async () => {
    const organizationUnitId = await Eva.getOrganizationUnitId(router.locale!);
    return await guestStockNotificationsGet({
      EmailAddress: emailAddress!,
      ProductID: productId,
      OrganizationUnitID: organizationUnitId,
    });
  });

  const createStockNotificationForCurrentUser = useMutation(
    async () => await userStockNotificationCreate({ ProductID: productId })
  );

  const createStockNotificationGuest = useMutation(async () => {
    const organizationUnitId = await Eva.getOrganizationUnitId(router.locale!);
    return await guestStockNotificationsCreate({
      EmailAddress: emailAddress!,
      ProductID: productId,
      OrganizationUnitID: organizationUnitId,
    });
  });

  // Exposed methods
  const getStockNotification = async ({ onSuccess, onError }: MutateOptions) => {
    if (user) {
      return getStockNotificationForCurrentUser.mutate(undefined, {
        onSuccess: onSuccess,
        onError: onError,
      });
    }

    return getStockNotificationGuest.mutate(undefined, {
      onSuccess: onSuccess,
      onError: onError,
    });
  };

  const createStockNotification = async ({ onSuccess, onError }: MutateOptions) => {
    if (user) {
      return createStockNotificationForCurrentUser.mutate(undefined, {
        onSuccess: onSuccess,
        onError: onError,
      });
    }

    return createStockNotificationGuest.mutate(undefined, {
      onSuccess: onSuccess,
      onError: onError,
    });
  };

  return {
    getStockNotification,
    createStockNotification,
  };
}
